import { eventDeletionMessage } from "@circle-react/components/Events/CompassEventsSpace/helpers";
import type { Event } from "@circle-react/types/Event";
import { useMutation } from "react-query";

import { t } from "@/i18n-js/instance";
import { eventApi } from "@/react/api/eventApi";
import type { RecurringEditMode } from "@/react/components/Events/helpers/constants";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { useCircleV4 } from "@/react/hooks/useCircleV4";
import { eventInvalidations } from "@/react/lib/queries/events";

interface UseDeleteEventMutationProps {
  event: Event;
  onDeleteError?: () => void;
  onPostDestroy: (...args: unknown[]) => void;
  onRefetch?: () => void | Promise<unknown>;
}

export const useDeleteEventMutation = ({
  event,
  onPostDestroy,
  onRefetch,
  onDeleteError,
}: UseDeleteEventMutationProps) => {
  const { isCircleV4 } = useCircleV4();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: (deleteMode: RecurringEditMode | "") =>
      eventApi.destroy({
        event,
        params: { delete_mode: deleteMode },
        forReactQuery: isCircleV4,
      }),
    onSuccess: () => {
      onPostDestroy(event.id);
      success(eventDeletionMessage(event), {
        shouldUseProgress: false,
      });
      void eventInvalidations.filterOptions();
      onRefetch?.()?.catch?.(() => undefined);
    },
    onError: () => {
      error(t("event_dropdown_actions.delete_failed"));
      if (isCircleV4) onDeleteError?.();
    },
  });
};
